import { createElement, useCallback, useEffect, useRef, useState } from 'react';

/** A draggable edge, as a hook so it can own drag state without a component
    boundary. Width is local while dragging and committed on release: a pref
    write per pointermove would hit localStorage a hundred times a second.

    `edge` is the side of the column the grip sits on. A grip on the left edge
    widens the column as it moves left; one on the right, as it moves right. */
export function useResizer({ width, onWidth, limits, clamp, edge, className, label }) {
  const [dragging, setDragging] = useState(null);
  const shown = dragging ?? width;
  const latest = useRef(shown);
  latest.current = shown;
  const sign = edge === 'left' ? -1 : 1;

  const start = useCallback(e => {
    e.preventDefault();
    const from = e.clientX;
    const base = latest.current;
    const move = ev => setDragging(clamp(base + sign * (ev.clientX - from)));
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      setDragging(null);
      onWidth(latest.current);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
  }, [onWidth, clamp, sign]);

  const nudge = e => {
    const dir = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
    if (!dir) return;
    e.preventDefault();
    onWidth(clamp(shown + sign * dir * limits.step));
  };

  useEffect(() => {
    if (dragging === null) return;
    // A drag that wanders over the page must not select it.
    document.body.classList.add('is-resizing');
    return () => document.body.classList.remove('is-resizing');
  }, [dragging]);

  return [shown, createElement('div', {
    className: className + (dragging !== null ? ' is-dragging' : ''),
    role: 'separator', 'aria-orientation': 'vertical', 'aria-label': label,
    'aria-valuenow': shown, 'aria-valuemin': limits.min, 'aria-valuemax': limits.max,
    tabIndex: 0, onPointerDown: start, onKeyDown: nudge
  })];
}
